import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { classNames } from '@progress/kendo-react-common';
import { GridFilterChangeEvent } from '@progress/kendo-react-grid';
import AutomationOptionsCard from '../components/automation-options-card';
import CoreSplitter from '@components-clean/core/core-splitter';
import ErrorComponent from '@components-clean/core/error';
import Grid from '@components-clean/core/grid-fe';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import { InfoField } from '@components-clean/core/info-field';
import Loading from '@components-clean/core/loading/loading';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import { DatasetDetails } from '@interfaces/central-monitoring';
import {
    ControlsAutomationColorEnumConvert,
    StudyAutomationColorEnumConvert,
} from '@interfaces-clean/study-color-automation.interface';
import IHubDataset, {
    AutomationTypes,
} from '@pages-clean/central-monitoring/dataset-hub/interfaces/hub-dataset.interface';
import Automation from '@pages-clean/central-monitoring/dataset-hub/tabs/automation';
import DatasetInfo from '@pages-clean/central-monitoring/dataset-hub/tabs/dataset-info';
import { COMPONENT_NAME as DATASET_NOTES_COMPONENT_NAME } from '@pages-clean/central-monitoring/dataset-hub/tabs/dataset-info/dataset-notes';
import { AuditApplicationArea, AuditDataType, AuditServiceType } from 'types/graphql-global-types';
import { useAuthorization, useLocalState } from '@hooks/index';
import { SplitterOrientation } from '@utils/constants';
import { stringDateWithoutTimeFromCurrentUtcDate } from '@utils/convertor';
import DatasetApi from '@apis/bff-api/central-monitoring/dataset';

enum Tabs {
    DataSetInfo,
    SiteAutomation,
    ControlsAutomation,
}

interface IProps {
    studyRef: string;
    studyNumber: string;
    selectedTab: Tabs;
    columns: GridColumnProps[];
}

const DatasetContent = ({ studyRef, studyNumber, selectedTab, columns }: IProps) => {
    const user = useAuthorization();
    if (!user) throw new Error('User not found');

    const { t } = useTranslation('cm', {
        keyPrefix: 'datasetHub',
    });

    const automationType =
        selectedTab === Tabs.DataSetInfo
            ? undefined
            : selectedTab === Tabs.SiteAutomation
              ? AutomationTypes.Site
              : AutomationTypes.Controls;

    interface IRow extends DatasetDetails {
        transformedAutomationDetails: {
            status: string;
            siteStatus: string;
            controlsStatus: string;
            siteTrigger: string;
            controlsTrigger: string;
            excludedSitesCsv: string;
            excludedPanelsCsv: string;
            reviewGeneratedOn: string;
        };
    }

    const { data, isLoading, error } = DatasetApi.useGetGrid(studyRef, automationType);

    const { t: tAdmin } = useTranslation('admin');
    const datasets: IRow[] = useMemo(() => {
        const updatedData = data?.map((dataset) => ({
            ...dataset,
            transformedAutomationDetails: {
                status: (dataset.automation?.status || null) ?? 'Not Run',
                siteStatus: (dataset.siteStatus || null) ?? 'Not Run',
                controlsStatus: dataset.controlsStatus ?? 'Not Run',
                siteTrigger: tAdmin(`${StudyAutomationColorEnumConvert(dataset.automation?.siteTrigger)}`),
                controlsTrigger: tAdmin(`${ControlsAutomationColorEnumConvert(dataset.automation?.controlsTrigger)}`),
                excludedSitesCsv: dataset.automation?.excludedSites?.map((site) => site.name).join(', ') ?? '',
                excludedPanelsCsv: dataset.automation?.excludedPanels?.map((panel) => panel.name).join(', ') ?? '',
                reviewGeneratedOn: dataset.automation?.reviewRevertedOn
                    ? ''
                    : (dataset.automation?.reviewGeneratedOn ?? ''),
            },
        }));

        return updatedData?.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()) ?? [];
    }, [data, selectedTab]);

    // Default to the currently selected dataset, or the most recent if none is selected.
    const { state } = useLocalState();

    if (!state) {
        return <ErrorComponent error={t('unexpectedError')} />;
    }
    const selectedDatasetId = state.selectedCM2Dataset?.id;
    const defaultSelectedDataset = useMemo(() => {
        if (datasets.length === 0) {
            return undefined;
        }

        if (selectedDatasetId) {
            return datasets.find((dataset) => dataset.id === selectedDatasetId);
        }

        return datasets[0];
    }, [datasets, selectedDatasetId]);

    const { setComponentHasChanges, componentHasChanges, confirmDiscardChanges } = useUnsavedChangesContext();
    const [selectedDataset, setSelectedDataset] = useState<IHubDataset | undefined>(defaultSelectedDataset);

    const handleFilterChange = async () => {
        if (componentHasChanges(DATASET_NOTES_COMPONENT_NAME)) {
            const confirmed = await confirmDiscardChanges();
            if (!confirmed) return false;
            setComponentHasChanges(DATASET_NOTES_COMPONENT_NAME, false);
        }
        return true;
    };
    const [gridFilter, setGridFilter] = useState<CompositeFilterDescriptor | undefined>(undefined);
    const panes = [{ size: '50%', min: '5px' }, { min: '35px' }];

    let selectedDatasetInfo;
    let exportFileName;
    let auditDescriptionPrefix;

    if (selectedTab === Tabs.DataSetInfo) {
        selectedDatasetInfo = <DatasetInfo studyRef={studyRef} dataset={selectedDataset} />;
        exportFileName = `Dataset Info_${studyNumber}_${stringDateWithoutTimeFromCurrentUtcDate('DD-MMM-yyyy')}`;
        auditDescriptionPrefix = 'CM - Dataset Hub';
    } else if (selectedTab === Tabs.SiteAutomation) {
        selectedDatasetInfo = (
            <Automation studyRef={studyRef} dataset={selectedDataset} automationType={AutomationTypes.Site} />
        );
        exportFileName = `Site Automation_${studyNumber}_${stringDateWithoutTimeFromCurrentUtcDate('DD-MMM-yyyy')}`;
        auditDescriptionPrefix = 'CM - Dataset Hub';
    } else if (selectedTab === Tabs.ControlsAutomation) {
        selectedDatasetInfo = (
            <Automation studyRef={studyRef} dataset={selectedDataset} automationType={AutomationTypes.Controls} />
        );
        exportFileName = `Controls Automation_${studyNumber}_${stringDateWithoutTimeFromCurrentUtcDate('DD-MMM-yyyy')}`;
        auditDescriptionPrefix = 'CM - Dataset Hub';
    }

    const displayAutomationCard = user.isObsAndLogsAdmin() && selectedTab === Tabs.SiteAutomation;

    let content;
    if (error) {
        content = <InfoField type="Error" message={t('loadError')} />;
    } else if (isLoading) {
        content = <Loading />;
    } else {
        content = (
            <div className="content">
                <CoreSplitter
                    orientation={SplitterOrientation.vertical}
                    data-testid="dataset-splitter"
                    alignmentValues={panes}
                    className="splitterHeight"
                >
                    <div className="k-h-full">
                        {displayAutomationCard && (
                            <div className="with-automation-config">
                                <AutomationOptionsCard studyRef={studyRef} />
                            </div>
                        )}
                        <Grid
                            className={classNames('dataset-grid', {
                                gridHeightWithAutomation: displayAutomationCard,
                            })}
                            title={t('grid.title')}
                            testDataId="dataset-hub-grid"
                            maxHeight="auto"
                            key={selectedTab}
                            selectable={{
                                enabled: true,
                                mode: 'single',
                            }}
                            defaultSelectedItem={selectedDataset}
                            preSelectionChange={async () => {
                                if (componentHasChanges(DATASET_NOTES_COMPONENT_NAME)) {
                                    const confirmed = await confirmDiscardChanges();
                                    if (!confirmed) {
                                        return false;
                                    }
                                    setComponentHasChanges(DATASET_NOTES_COMPONENT_NAME, false);
                                }
                                return true;
                            }}
                            onSelectionChange={({ startDataItem }) => {
                                const dataset = startDataItem as DatasetDetails;
                                setSelectedDataset(dataset);
                            }}
                            dataItemKey="id"
                            scrollable="scrollable"
                            exportFilename={exportFileName}
                            service={AuditServiceType.CENTRAL_MONITORING_2}
                            area={AuditApplicationArea.CENTRAL_MONITORING_2}
                            dataType={AuditDataType.EXCEL_EXPORT}
                            auditDescriptionPrefix={auditDescriptionPrefix}
                            columns={columns ?? []}
                            data={datasets}
                            preventAutoscroll
                            filter={gridFilter}
                            onFilterChange={async (e: GridFilterChangeEvent) => {
                                const proceed = await handleFilterChange();
                                if (proceed) {
                                    setGridFilter(e.filter);
                                }
                            }}
                            clearFilter={async () => handleFilterChange()}
                            showAddIcon={false}
                        />
                    </div>
                    {selectedDatasetInfo}
                </CoreSplitter>
            </div>
        );
    }

    return content;
};

export default DatasetContent;
